import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger safely for React
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

// -------------------------------------------------------------------------
// THEME & COMPONENT STYLES (Self-contained, pure CSS - zero Tailwind dependency)
// -------------------------------------------------------------------------
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');

/* Curtain Reveal Outer Wrapper */
.cinematic-curtain-wrapper {
  position: relative;
  height: 100vh;
  clip-path: polygon(0% 0, 100% 0%, 100% 100%, 0 100%);
}

/* Fixed Footer Container */
.cinematic-footer-fixed {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  background-color: #070D16;
  color: #F3EDE3;
  font-family: 'Plus Jakarta Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
  box-sizing: border-box;
}

/* Aurora Glow Background */
.footer-aurora {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 85vw;
  height: 65vh;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    circle at 50% 50%, 
    rgba(201, 75, 42, 0.22) 0%, 
    rgba(12, 21, 34, 0.35) 40%, 
    transparent 70%
  );
  border-radius: 50%;
  filter: blur(90px);
  pointer-events: none;
  z-index: 0;
  animation: footer-breathe 8s ease-in-out infinite alternate;
}

@keyframes footer-breathe {
  0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
  100% { transform: translate(-50%, -50%) scale(1.1); opacity: 1; }
}

/* Grid Pattern Overlay */
.footer-bg-grid {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-size: 60px 60px;
  background-image: 
    linear-gradient(to right, rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
  mask-image: linear-gradient(to bottom, transparent, black 30%, black 70%, transparent);
  -webkit-mask-image: linear-gradient(to bottom, transparent, black 30%, black 70%, transparent);
}

/* Giant Background Text */
.footer-giant-bg-text {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  font-size: clamp(80px, 20vw, 280px);
  line-height: 0.8;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: transparent;
  -webkit-text-stroke: 1px rgba(255, 255, 255, 0.06);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.07) 0%, transparent 70%);
  -webkit-background-clip: text;
  background-clip: text;
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
  z-index: 0;
}

/* Marquee Ribbon */
.footer-marquee-ribbon {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(7, 13, 22, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding: 14px 0;
  z-index: 10;
  flex-shrink: 0;
}

.footer-marquee-content {
  display: flex;
  width: max-content;
  animation: footer-scroll-marquee 35s linear infinite;
  font-size: clamp(11px, 1.2vw, 13px);
  font-weight: 700;
  letter-spacing: 0.25em;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
}

@keyframes footer-scroll-marquee {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}

.marquee-group {
  display: flex;
  align-items: center;
  gap: 48px;
  padding: 0 24px;
}

.marquee-dot {
  color: rgba(201, 75, 42, 0.8);
}

/* Main Content Area */
.footer-main-content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  flex: 1;
  box-sizing: border-box;
}

/* Main Heading */
.footer-heading {
  font-size: clamp(32px, 5.5vw, 68px);
  font-weight: 800;
  letter-spacing: -0.02em;
  margin-bottom: 36px;
  text-align: center !important;
  width: 100%;
  line-height: 1.15;
  background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0.5) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0px 0px 24px rgba(255, 255, 255, 0.15));
}

/* Interactive Pills Layout */
.footer-pills-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
}

.footer-pills-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  width: 100%;
}

/* Glass Pill Button */
.footer-glass-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 14px;
  text-decoration: none;
  color: #F3EDE3;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%);
  box-shadow: 
      0 10px 30px -10px rgba(0, 0, 0, 0.4), 
      inset 0 1px 1px rgba(255, 255, 255, 0.12), 
      inset 0 -1px 2px rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;
  box-sizing: border-box;
}

.footer-glass-pill:hover {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 100%);
  border-color: rgba(255, 255, 255, 0.25);
  box-shadow: 
      0 20px 40px -10px rgba(0, 0, 0, 0.6), 
      inset 0 1px 1px rgba(255, 255, 255, 0.25);
  color: #ffffff;
}

.footer-pill-primary {
  background: linear-gradient(145deg, rgba(201, 75, 42, 0.4) 0%, rgba(201, 75, 42, 0.2) 100%);
  border-color: rgba(201, 75, 42, 0.5);
  padding: 18px 36px;
  font-size: 15px;
  color: #ffffff;
}

.footer-pill-primary:hover {
  background: linear-gradient(145deg, rgba(201, 75, 42, 0.6) 0%, rgba(201, 75, 42, 0.35) 100%);
  border-color: rgba(201, 75, 42, 0.7);
}

.footer-pill-secondary {
  padding: 14px 28px;
  font-size: 14px;
}

.footer-pill-sub {
  padding: 10px 22px;
  font-size: 13px;
  color: rgba(243, 237, 227, 0.65);
}

.footer-pill-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #C94B2A;
  box-shadow: 0 0 10px #C94B2A;
  animation: footer-dot-pulse 2s infinite;
}

@keyframes footer-dot-pulse {
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.3); opacity: 1; }
}

/* Bottom Bar */
.footer-bottom-bar {
  position: relative;
  z-index: 20;
  width: 100%;
  padding: 16px clamp(24px, 4vw, 48px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  flex-shrink: 0;
  box-sizing: border-box;
}

.footer-copyright {
  color: rgba(243, 237, 227, 0.45);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.footer-badge {
  padding: 8px 18px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(243, 237, 227, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
}

.footer-heart {
  color: #C94B2A;
  animation: footer-heartbeat 2s cubic-bezier(0.25, 1, 0.5, 1) infinite;
}

@keyframes footer-heartbeat {
  0%, 100% { transform: scale(1); filter: drop-shadow(0 0 5px rgba(201, 75, 42, 0.5)); }
  15%, 45% { transform: scale(1.25); filter: drop-shadow(0 0 10px rgba(201, 75, 42, 0.8)); }
  30% { transform: scale(1); }
}

.footer-back-to-top {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(243, 237, 227, 0.7);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  cursor: pointer;
  transition: all 0.3s ease;
}

.footer-back-to-top:hover {
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}
`;

// -------------------------------------------------------------------------
// MAGNETIC BUTTON PRIMITIVE
// -------------------------------------------------------------------------
export const MagneticButton = React.forwardRef(
  ({ className = '', style, children, as: Component = 'button', onClick, href, ...props }, forwardedRef) => {
    const localRef = useRef(null);

    useEffect(() => {
      if (typeof window === 'undefined') return;
      const element = localRef.current;
      if (!element) return;

      const ctx = gsap.context(() => {
        const handleMouseMove = (e) => {
          const rect = element.getBoundingClientRect();
          const h = rect.width / 2;
          const w = rect.height / 2;
          const x = e.clientX - rect.left - h;
          const y = e.clientY - rect.top - w;

          gsap.to(element, {
            x: x * 0.35,
            y: y * 0.35,
            rotationX: -y * 0.12,
            rotationY: x * 0.12,
            scale: 1.04,
            ease: 'power2.out',
            duration: 0.4,
          });
        };

        const handleMouseLeave = () => {
          gsap.to(element, {
            x: 0,
            y: 0,
            rotationX: 0,
            rotationY: 0,
            scale: 1,
            ease: 'elastic.out(1, 0.3)',
            duration: 1.2,
          });
        };

        element.addEventListener('mousemove', handleMouseMove);
        element.addEventListener('mouseleave', handleMouseLeave);

        return () => {
          element.removeEventListener('mousemove', handleMouseMove);
          element.removeEventListener('mouseleave', handleMouseLeave);
        };
      }, element);

      return () => ctx.revert();
    }, []);

    const combinedClassName = `footer-glass-pill ${className}`.trim();

    return (
      <Component
        ref={(node) => {
          localRef.current = node;
          if (typeof forwardedRef === 'function') forwardedRef(node);
          else if (forwardedRef) forwardedRef.current = node;
        }}
        className={combinedClassName}
        style={style}
        onClick={onClick}
        href={href}
        {...props}
      >
        {children}
      </Component>
    );
  }
);
MagneticButton.displayName = 'MagneticButton';

// -------------------------------------------------------------------------
// MARQUEE ITEM
// -------------------------------------------------------------------------
const MarqueeGroup = () => (
  <div className="marquee-group">
    <span>Accountability Redefined</span> <span className="marquee-dot">✦</span>
    <span>AI Industrial Safety</span> <span className="marquee-dot">✦</span>
    <span>Real-Time Floor Alerts</span> <span className="marquee-dot">✦</span>
    <span>Works With Existing CCTV</span> <span className="marquee-dot">✦</span>
    <span>Zero Hardware Cost</span> <span className="marquee-dot">✦</span>
    <span>Audit-Ready Logs</span> <span className="marquee-dot">✦</span>
  </div>
);

// -------------------------------------------------------------------------
// CINEMATIC FOOTER COMPONENT
// -------------------------------------------------------------------------
export function CinematicFooter() {
  const wrapperRef = useRef(null);
  const giantTextRef = useRef(null);
  const headingRef = useRef(null);
  const linksRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!wrapperRef.current) return;

    const ctx = gsap.context(() => {
      // Parallax effect on giant text
      gsap.fromTo(
        giantTextRef.current,
        { xPercent: -50, y: '10vh', scale: 0.85, opacity: 0 },
        {
          xPercent: -50,
          y: '0vh',
          scale: 1,
          opacity: 1,
          ease: 'power1.out',
          scrollTrigger: {
            trigger: wrapperRef.current,
            start: 'top 85%',
            end: 'bottom bottom',
            scrub: 1,
          },
        }
      );

      // Staggered reveal for heading and buttons
      gsap.fromTo(
        [headingRef.current, linksRef.current],
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: wrapperRef.current,
            start: 'top 50%',
            end: 'bottom bottom',
            scrub: 1,
          },
        }
      );
    }, wrapperRef);

    return () => ctx.revert();
  }, []);

  const scrollToTop = () => {
    if (window.lenis) {
      window.lenis.scrollTo(0, { duration: 1.4 });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {/* Curtain reveal outer wrapper */}
      <div ref={wrapperRef} className="cinematic-curtain-wrapper">

        {/* Fixed footer underneath */}
        <footer className="cinematic-footer-fixed">

          {/* Ambient Aurora Glow */}
          <div className="footer-aurora" />

          {/* Grid Pattern Overlay */}
          <div className="footer-bg-grid" />

          {/* Giant Parallax Background Text */}
          <div ref={giantTextRef} className="footer-giant-bg-text">
            TRUVUE AI
          </div>

          {/* Top Marquee Ribbon */}
          <div className="footer-marquee-ribbon">
            <div className="footer-marquee-content">
              <MarqueeGroup />
              <MarqueeGroup />
            </div>
          </div>

          {/* Main Center Content */}
          <div className="footer-main-content">
            <h2 ref={headingRef} className="footer-heading">
              Ready to elevate site safety?
            </h2>

            {/* Magnetic Pills Layout */}
            <div ref={linksRef} className="footer-pills-container">
              {/* Primary Call to Action */}
              <div className="footer-pills-row">
                <MagneticButton
                  as="a"
                  href="https://wa.me/919425074535"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-pill-primary"
                >
                  <span className="footer-pill-dot" />
                  Book a Live Demo
                </MagneticButton>

                <MagneticButton
                  as="a"
                  href="#platform"
                  className="footer-pill-secondary"
                >
                  Explore Platform →
                </MagneticButton>
              </div>

              {/* Secondary Links */}
              <div className="footer-pills-row">
                <MagneticButton as="a" href="#security" className="footer-pill-sub">
                  Security & Compliance
                </MagneticButton>
                <MagneticButton as="a" href="#gallery" className="footer-pill-sub">
                  Gallery
                </MagneticButton>
                <MagneticButton as="a" href="#why-truvue" className="footer-pill-sub">
                  Why TruVue AI
                </MagneticButton>
                <MagneticButton as="a" href="#faq" className="footer-pill-sub">
                  FAQ
                </MagneticButton>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="footer-bottom-bar">
            {/* Copyright */}
            <div className="footer-copyright">
              © 2026 TruVue AI. All rights reserved.
            </div>

            {/* Badge */}
            {/* <div className="footer-badge">
              <span>Built with</span>
              <span className="footer-heart">❤</span>
              <span>for Industrial Excellence</span>
            </div> */}

            {/* Back to top button */}
            <MagneticButton
              as="button"
              onClick={scrollToTop}
              aria-label="Scroll back to top"
              className="footer-back-to-top"
            >
              <svg style={{ width: 20, height: 20 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </MagneticButton>
          </div>

        </footer>
      </div>
    </>
  );
}

export default CinematicFooter;
